import React, { useState, useEffect } from "react";
import axios from "axios";
import { Slider, Button, Checkbox, Form, message, Typography, Divider } from "antd";
import { BsNutFill } from 'react-icons/bs';
import { NODE_BACKEND_API_URL } from "../../config";

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Text, Title } = Typography;

function VolumePricing({ onVolumeUpdate }) {
    const [form] = Form.useForm();
    // State for the form data - initialize with null to indicate not loaded yet
    const [formData, setFormData] = useState({
        rangeValue1: null,
        markValue1: null,
        rangeValue2: null,
        markValue2: null
    });

    // State for loading and data management
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [data, setData] = useState([]);

    // API endpoints
    const fetchUrl = `${NODE_BACKEND_API_URL}/getvolumebasedprices/`;
    const updateUrl = `${NODE_BACKEND_API_URL}/updatevolumebasedprices/`;

    // Helper function to fetch current volume pricing data from backend
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(fetchUrl);
            setData(res.data);
            
            // Check if data exists - the API returns a single object, not an array
            if (res.data && typeof res.data === 'object') {
                const currentData = res.data;
                const initialValues = {
                    rangeValue1: currentData.RangeValue1 || 50,
                    markValue1: currentData.MarkValue1 || 50,
                    rangeValue2: currentData.RangeValue2 || 70,
                    markValue2: currentData.MarkValue2 || 30,
                    confirmUpdate: false
                };
                setFormData(initialValues);
                form.setFieldsValue(initialValues);
                console.log("Volume pricing data loaded:", currentData);
            } else {
                // If no data exists in backend, use default values
                const defaultValues = {
                    rangeValue1: 50,
                    markValue1: 50,
                    rangeValue2: 70,
                    markValue2: 30,
                    confirmUpdate: false
                };
                setFormData(defaultValues);
                form.setFieldsValue(defaultValues);
                console.log("No data found, using defaults");
            }
        } catch (er) {
            console.error("Error fetching volume pricing data:", er);
            setData([]);
            // Set default values on error
            const defaultValues = {
                rangeValue1: 50,
                markValue1: 50,
                rangeValue2: 70,
                markValue2: 30,
                confirmUpdate: false
            };
            setFormData(defaultValues);
            form.setFieldsValue(defaultValues);
        } finally {
            setLoading(false);
        }
    };

    // Effect hook to fetch data when component mounts
    useEffect(() => {
        fetchData();
    }, [fetchUrl]);

    // Handler for slider changes
    function handleSliderChange(field, value) {
        setFormData(prevFormData => {
            const newFormData = { ...prevFormData, [field]: value };
            
            // Ensure rangeValue2 is always >= rangeValue1
            if (field === 'rangeValue1' && value > (newFormData.rangeValue2 || 0)) {
                newFormData.rangeValue2 = value;
                form.setFieldsValue({ rangeValue2: value });
            }
            
            return newFormData;
        });
    }

    // Function to handle form submission
    const handleSubmit = async (values) => {
        setSubmitting(true);

        // Construct the data payload to send to the backend
        const payload = {
            rangeValue1: formData.rangeValue1,
            markValue1: formData.markValue1,
            rangeValue2: formData.rangeValue2,
            markValue2: formData.markValue2
        };

        try {
            const res = await axios.put(updateUrl, payload);
            console.log("Update successful:", res.data);
            message.success(
                `Variables por Volumen Actualizadas:\nRango 1: ${formData.rangeValue1} unidades, ${formData.markValue1}% markup\nRango 2: ${formData.rangeValue2} unidades, ${formData.markValue2}% markup`
            );

            // Call the callback function to notify the parent
            if (onVolumeUpdate) {
                onVolumeUpdate(formData);
            }

            // Refresh data after successful update
            fetchData();
        } catch (error) {
            console.error("Update failed:", error);
            if (error.response) {
                message.error(`Error: ${error.response.data.msg || error.response.statusText}`);
            } else if (error.request) {
                message.error('No se recibió respuesta del servidor. Verifique la conexión.');
            } else {
                message.error(`Error: ${error.message}`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Render loading state
    if (loading || formData.rangeValue1 === null) {
        return (
            <div className="editor-position" style={{ position: 'sticky', top: '10px', zIndex: 10 }}>
                <BsNutFill size={42} />
                <h2>PRECIOS POR VOLUMEN</h2>
                <div className="variable-editor-div" style={{ background: '#f2673a' }}>
                    <p style={{ color: 'white' }}>Cargando datos de precios por volumen...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="editor-position" style={{ position: 'sticky', top: '10px', zIndex: 10 }}>
            <BsNutFill size={42} />
            <h2>PRECIOS POR VOLUMEN</h2>
            <div className="variable-editor-div volume-pricing-card" style={{ background: '#f2673a' }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    {/* Rango 1 Section */}
                    <Title level={5} style={{ color: 'white', marginTop: 0 }}>
                        Rango 1
                    </Title>
                    
                    {/* Range Value 1 Slider */}
                    <Form.Item
                        label={<Text style={{ color: 'white' }}>Hasta: {formData.rangeValue1} unidades</Text>}
                        name="rangeValue1"
                    >
                        <Slider
                            className="volume-pricing-slider"
                            marks={{
                                10: { style: { color: 'white' }, label: '10' },
                                50: { style: { color: 'white' }, label: '50' },
                                100: { style: { color: 'white' }, label: '100' }
                            }}
                            step={10}
                            min={10}
                            max={100}
                            tooltip={{ formatter: (value) => `${value} unidades` }}
                            onChange={(value) => handleSliderChange('rangeValue1', value)}
                        />
                    </Form.Item>

                    {/* Mark Value 1 Slider */}
                    <Form.Item
                        label={<Text style={{ color: 'white' }}>Markup: {formData.markValue1}%</Text>}
                        name="markValue1"
                        style={{ marginBottom: '2rem' }}
                    >
                        <Slider
                            className="volume-pricing-slider"
                            marks={{
                                25: { style: { color: 'white' }, label: '25%' },
                                50: { style: { color: 'white' }, label: '50%' },
                                75: { style: { color: 'white' }, label: '75%' },
                                100: { style: { color: 'white' }, label: '100%' }
                            }}
                            step={5}
                            min={25}
                            max={100}
                            tooltip={{ formatter: (value) => `${value}%` }}
                            onChange={(value) => handleSliderChange('markValue1', value)}
                        />
                    </Form.Item>

                    {/* Visual Separator between Rango 1 and Rango 2 */}
                    <Divider style={{ borderColor: 'rgba(255, 255, 255, 0.3)', margin: '2rem 0' }} />

                    {/* Rango 2 Section */}
                    <Title level={5} style={{ color: 'white', marginTop: 0 }}>
                        Rango 2
                    </Title>
                    
                    {/* Range Value 2 Slider */}
                    <Form.Item
                        label={<Text style={{ color: 'white' }}>Desde {formData.rangeValue1} hasta: {formData.rangeValue2} unidades</Text>}
                        name="rangeValue2"
                    >
                        <Slider
                            className="volume-pricing-slider"
                            marks={{
                                [formData.rangeValue1]: { style: { color: 'white' }, label: String(formData.rangeValue1) },
                                100: { style: { color: 'white' }, label: '100' },
                                150: { style: { color: 'white' }, label: '150' }
                            }}
                            step={10}
                            min={formData.rangeValue1}
                            max={150}
                            tooltip={{ formatter: (value) => `${value} unidades` }}
                            onChange={(value) => handleSliderChange('rangeValue2', value)}
                        />
                    </Form.Item>

                    {/* Mark Value 2 Slider */}
                    <Form.Item
                        label={<Text style={{ color: 'white' }}>Markup: {formData.markValue2}%</Text>}
                        name="markValue2"
                        style={{ marginBottom: '2rem' }}
                    >
                        <Slider
                            className="volume-pricing-slider"
                            marks={{
                                25: { style: { color: 'white' }, label: '25%' },
                                50: { style: { color: 'white' }, label: '50%' },
                                75: { style: { color: 'white' }, label: '75%' },
                                100: { style: { color: 'white' }, label: '100%' }
                            }}
                            step={5}
                            min={25}
                            max={100}
                            tooltip={{ formatter: (value) => `${value}%` }}
                            onChange={(value) => handleSliderChange('markValue2', value)}
                        />
                    </Form.Item>

                    {/* Checkbox and Button */}
                    <Form.Item
                        name="confirmUpdate"
                        valuePropName="checked"
                        rules={[
                            {
                                validator: (_, value) =>
                                    value ? Promise.resolve() : Promise.reject(new Error('Debe confirmar los cambios')),
                            },
                        ]}
                    >
                        <Checkbox style={{ color: 'white' }}>
                            <span style={{ color: 'white' }}>Confirmar cambios</span>
                        </Checkbox>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                            type="primary"
                            htmlType={isDemoMode ? 'button' : 'submit'}
                            onClick={isDemoMode ? () => message.info('Actualizar precios por volumen — disponible en la versión completa del sistema') : undefined}
                            loading={submitting}
                            block
                            size="large"
                            className="volume-pricing-submit-btn"
                        >
                            ACTUALIZAR VARIABLES POR VOLUMEN
                        </Button>
                    </Form.Item>

                </Form>
            </div>
        </div>
    );
}

export default VolumePricing;